/**
 * Token Storage Utility
 *
 * Handles authentication token storage with support for both:
 * - sessionStorage: Token expires when browser closes (rememberMe = false)
 * - localStorage: Token persists across sessions (rememberMe = true)
 */

const TOKEN_KEY = "auth_token";
const REMEMBER_ME_KEY = "remember_me";

export const tokenStorage = {
  /**
   * Get the authentication token from either storage
   * @returns The token if found, null otherwise
   */
  getToken(): null | string {
    // Check sessionStorage first (current session)
    const sessionToken = sessionStorage.getItem(TOKEN_KEY);
    if (sessionToken) {
      return sessionToken;
    }

    // Fall back to localStorage (persistent)
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Check if a token exists in either storage
   * @returns true if a token exists
   */
  hasToken(): boolean {
    return !!this.getToken();
  },

  /**
   * Check if remember me was enabled
   * @returns true if remember me is enabled
   */
  isRememberMeEnabled(): boolean {
    return localStorage.getItem(REMEMBER_ME_KEY) === "true";
  },

  /**
   * Remove the authentication token from both storages
   */
  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
  },

  /**
   * Set the authentication token
   * @param token - The authentication token
   * @param rememberMe - Whether to persist the token across browser sessions
   */
  setToken(token: string, rememberMe: boolean = false): void {
    // Store the remember me preference
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());

    if (rememberMe) {
      // Persist token across browser sessions
      localStorage.setItem(TOKEN_KEY, token);
      // Clear from sessionStorage to avoid conflicts
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      // Token only valid for current browser session
      sessionStorage.setItem(TOKEN_KEY, token);
      // Clear from localStorage to avoid conflicts
      localStorage.removeItem(TOKEN_KEY);
    }
  },
};
